import { Checkbox, Input, Label, MultiSelect, RadioGroup, RadioGroupItem } from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { FormResponseValue } from "@tellescope/types-models";
import { TellescopeFormFieldBaseProps } from "../../types";
import { resolveFieldOptionDetails, resolveVisibleChoices } from "../../utils";
import type { NextFieldLogicOptions } from "../../utils/types";
import { FieldDescription } from "../../FieldDescription";
import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { OptionRowLabel } from "./OptionRowLabel";
import { CardOptionButton } from "./CardOptionButton";

// Radio value standing in for the free-text "Other" entry. Namespaced to make collision with a
// real choice label effectively impossible, since choices are author-supplied free text.
const OTHER_SENTINEL = "__tellescope_other_option__";

// Tags the Other entry while it is held in component-internal arrays, so it stays identifiable
// even when the typed text is byte-identical to a real choice label. Answers are still derived
// from `value` rather than stored, but the tag is stripped by `emitAnswer` before anything
// reaches `onChange` — the submitted array keeps the plain shape that scoring, conditional logic,
// and the server already read.
const OTHER_PREFIX = "__tellescope_other_text__:";
const tagOther = (text: string) => `${OTHER_PREFIX}${text}`;
const isTaggedOther = (v: string) => v.startsWith(OTHER_PREFIX);
const stripOtherTag = (v: string) => (isTaggedOther(v) ? v.slice(OTHER_PREFIX.length) : v);

export interface MultipleChoiceFieldProps extends TellescopeFormFieldBaseProps<"multiple_choice"> {
  checkboxList?: boolean;
  formResponses?: FormResponseValue[];
  logicOptions?: NextFieldLogicOptions;
}

export const MultipleChoiceField = ({
  title,
  description,
  htmlDescription,
  value = [],
  onChange,
  className,
  options,
  error,
  touched,
  required,
  checkboxList,
  formResponses,
  logicOptions,
  autoFocus,
}: MultipleChoiceFieldProps) => {
  const { t } = useTranslation("forms");
  const showError = !!(error && touched);
  const optionDetails = useMemo(
    () =>
      resolveVisibleChoices({
        optionDetails: resolveFieldOptionDetails(options),
        responses: formResponses ?? [],
        logicOptions,
      }),
    [options, formResponses, logicOptions]
  );
  const allowOther = !!options?.other;
  const showCardButtons = !!options?.autoAdvance && !!options?.radio;

  const fieldId = useId();

  const knownOptionValues = useMemo(() => optionDetails.map((o) => o.option), [optionDetails]);
  // Every choice the author defined, not just the currently visible ones. `knownOptionValues` is
  // narrowed by conditional logic, so a hidden-but-selected choice would otherwise look unknown
  // and be misread as the Other answer.
  const authoredOptionValues = useMemo(() => resolveFieldOptionDetails(options).map((o) => o.option), [options]);
  const [otherText, setOtherText] = useState("");
  const otherFromValue = useMemo(() => {
    const tagged = value.find(isTaggedOther);
    if (tagged) return stripOtherTag(tagged);
    // Text typed this session identifies the Other entry even when it collides with a choice
    // label. Across a reload nothing distinguishes them, and the entry reads as a choice.
    if (otherText && value.includes(otherText)) return otherText;
    return value.find((v) => !authoredOptionValues.includes(v)) ?? "";
  }, [value, authoredOptionValues, otherText]);
  const effectiveOtherText = otherText || otherFromValue;

  // `value` arrives untagged (it round-trips through submission), so the Other entry is re-tagged
  // here before any handler manipulates the array. Only the first match is tagged: a colliding
  // label can appear twice, once as a real selection and once as the Other text.
  const taggedValue = useMemo(() => {
    if (!otherFromValue) return value;
    const at = value.lastIndexOf(otherFromValue);
    return value.map((v, i) => (i === at && !isTaggedOther(v) ? tagOther(v) : v));
  }, [value, otherFromValue]);
  const emitAnswer = (next: string[]) => onChange(next.map(stripOtherTag));
  // Derived from the answer rather than stored, so an Other answer that loads after mount (the
  // responses query resolves independently of the fields query) still shows as checked. The
  // override covers what the answer can't express on its own: Other checked before any text is
  // typed, and Other explicitly dismissed while `value` still holds the previous text, since the
  // parent re-renders a tick later.
  const [isOtherCheckedOverride, setIsOtherCheckedOverride] = useState<boolean | undefined>(undefined);
  const isOtherChecked = isOtherCheckedOverride ?? !!otherFromValue;

  const isExclusiveChoice = (choice: string) => !!options?.radio || !!options?.radioChoices?.includes(choice);

  const handleChoiceToggle = (choice: string, checked: boolean) => {
    if (!checked) {
      emitAnswer(isExclusiveChoice(choice) ? [] : taggedValue.filter((v) => isTaggedOther(v) || v !== choice));
      return;
    }
    if (isExclusiveChoice(choice)) {
      emitAnswer([choice]);
      return;
    }
    emitAnswer([...taggedValue.filter((v) => isTaggedOther(v) || !isExclusiveChoice(v)), choice]);
  };

  const handleOtherTextChange = (next: string) => {
    setOtherText(next);
    setIsOtherCheckedOverride(true);
    const withoutOther = taggedValue.filter((v) => !isTaggedOther(v));
    if (options?.radio) {
      emitAnswer(next ? [tagOther(next)] : []);
    } else {
      emitAnswer(next ? [...withoutOther, tagOther(next)] : withoutOther);
    }
  };

  const handleOtherToggle = (checked: boolean) => {
    setIsOtherCheckedOverride(checked);
    const withoutOther = taggedValue.filter((v) => !isTaggedOther(v));
    if (checked) {
      const text = effectiveOtherText || "";
      if (options?.radio) {
        emitAnswer(text ? [tagOther(text)] : []);
      } else {
        emitAnswer(text ? [...withoutOther, tagOther(text)] : withoutOther);
      }
    } else {
      setOtherText("");
      emitAnswer(withoutOther);
    }
  };

  const handleCardSelect = (choice: string, isSelected: boolean) => {
    setIsOtherCheckedOverride(false);
    setOtherText("");
    emitAnswer(isSelected ? [] : [choice]);
  };

  if (showCardButtons) {
    const selectedRadio = isOtherChecked ? undefined : value[0];
    return (
      <div className="space-y-3">
        {!!title && <Label required={required}>{title}</Label>}
        <FieldDescription description={description} htmlDescription={htmlDescription} />
        <div className={cn("flex flex-col gap-2", className)}>
          {optionDetails.map((option, index) => (
            <CardOptionButton
              key={option.option}
              option={option}
              selected={selectedRadio === option.option}
              onSelect={() => handleCardSelect(option.option, selectedRadio === option.option)}
              invalid={showError}
              autoFocus={autoFocus && index === 0}
            />
          ))}
          {allowOther && (
            <CardOptionButton
              option={{ option: t("Other") }}
              selected={isOtherChecked}
              onSelect={() => handleOtherToggle(!isOtherChecked)}
              invalid={showError}
              autoFocus={autoFocus && optionDetails.length === 0}
            />
          )}
        </div>
        {isOtherChecked && (
          <Input
            value={effectiveOtherText}
            onChange={(e) => handleOtherTextChange(e.target.value)}
            placeholder={t("Please specify")}
            className={cn(showError && "border-destructive")}
            aria-invalid={showError}
          />
        )}
        {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
      </div>
    );
  }

  if (options?.radio) {
    const radioValue = isOtherChecked ? OTHER_SENTINEL : value[0] ?? "";
    return (
      <div className="space-y-3">
        {!!title && <Label required={required}>{title}</Label>}
        <FieldDescription description={description} htmlDescription={htmlDescription} />
        <RadioGroup
          value={radioValue}
          onValueChange={(val) => {
            if (val === OTHER_SENTINEL) {
              handleOtherToggle(true);
            } else {
              setIsOtherCheckedOverride(false);
              setOtherText("");
              emitAnswer([val]);
            }
          }}
          className={className}
        >
          {optionDetails.map((option, index) => {
            const id = `${fieldId}-${option.option}`;
            return (
              <div
                key={option.option}
                className={cn("flex gap-2", option.description ? "items-start" : "items-center")}
              >
                <RadioGroupItem
                  id={id}
                  value={option.option}
                  onClick={() => {
                    if (!isOtherChecked && value[0] === option.option) emitAnswer([]);
                  }}
                  className={cn(showError && "border-destructive")}
                  autoFocus={autoFocus && index === 0}
                />
                <Label htmlFor={id} className="cursor-pointer font-normal">
                  <OptionRowLabel option={option.option} description={option.description} />
                </Label>
              </div>
            );
          })}
          {allowOther && (
            <div className="flex items-center gap-2">
              {/* Unlike the choice items above, Other is intentionally not click-deselectable;
                  clear the text input instead. */}
              <RadioGroupItem
                id={`${fieldId}-other`}
                value={OTHER_SENTINEL}
                className={cn(showError && "border-destructive")}
              />
              <Label htmlFor={`${fieldId}-other`} className="cursor-pointer font-normal">
                {t("Other")}
              </Label>
            </div>
          )}
        </RadioGroup>
        {isOtherChecked && (
          <Input
            value={effectiveOtherText}
            onChange={(e) => handleOtherTextChange(e.target.value)}
            placeholder={t("Please specify")}
            className={cn(showError && "border-destructive")}
            aria-invalid={showError}
          />
        )}
        {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
      </div>
    );
  }

  if (checkboxList) {
    return (
      <div className="space-y-3">
        {!!title && <Label required={required}>{title}</Label>}
        <FieldDescription description={description} htmlDescription={htmlDescription} />
        <div className={cn("space-y-2", className)}>
          {optionDetails.map((option, index) => {
            const id = `${fieldId}-${option.option}`;
            const isChecked = taggedValue.some((v) => !isTaggedOther(v) && v === option.option);

            return (
              <div
                key={option.option}
                className={cn("flex gap-2", option.description ? "items-start" : "items-center")}
              >
                <Checkbox
                  id={id}
                  checked={isChecked}
                  onCheckedChange={(checked) => handleChoiceToggle(option.option, !!checked)}
                  className={cn(showError && "border-destructive")}
                  autoFocus={autoFocus && index === 0}
                />
                <Label htmlFor={id} className="cursor-pointer font-normal">
                  <OptionRowLabel option={option.option} description={option.description} />
                </Label>
              </div>
            );
          })}
          {allowOther && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${fieldId}-other`}
                checked={isOtherChecked}
                onCheckedChange={(checked) => handleOtherToggle(!!checked)}
                className={cn(showError && "border-destructive")}
              />
              <Label htmlFor={`${fieldId}-other`} className="cursor-pointer font-normal">
                {t("Other")}
              </Label>
            </div>
          )}
        </div>
        {isOtherChecked && (
          <Input
            value={effectiveOtherText}
            onChange={(e) => handleOtherTextChange(e.target.value)}
            placeholder={t("Please specify")}
            className={cn(showError && "border-destructive")}
            aria-invalid={showError}
            autoFocus
          />
        )}
        {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {!!title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <MultiSelect
        value={taggedValue.filter((v) => !isTaggedOther(v) && knownOptionValues.includes(v))}
        onValueChange={(newVal) => {
          emitAnswer(effectiveOtherText && isOtherChecked ? [...newVal, tagOther(effectiveOtherText)] : newVal);
        }}
        className={cn(className, showError && "border-destructive")}
        options={optionDetails.map((option) => ({
          value: option.option,
          label: `${option.option}${option.description ? ` — ${option.description}` : ""}`,
        }))}
        searchKeywords={(option) => [option.value, option.label]}
        minPopoverWidth="250px"
        autoFocus={autoFocus}
      />
      {allowOther && (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${fieldId}-other`}
            checked={isOtherChecked}
            onCheckedChange={(checked) => handleOtherToggle(!!checked)}
            className={cn(showError && "border-destructive")}
          />
          <Label htmlFor={`${fieldId}-other`} className="cursor-pointer font-normal">
            {t("Other")}
          </Label>
        </div>
      )}
      {isOtherChecked && (
        <Input
          value={effectiveOtherText}
          onChange={(e) => handleOtherTextChange(e.target.value)}
          placeholder={t("Please specify")}
          className={cn(showError && "border-destructive")}
          aria-invalid={showError}
        />
      )}
      {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
